import { cn } from "@/lib/utils";
import { Sparkles, ShieldCheck } from "lucide-react";

/**
 * Deterministic decision block.
 * Sharp edges, slate palette, explicit rule reference.
 * Used to represent business-rule outcomes that AI must NEVER override.
 */
export function DeterministicDecision({ ruleId, ruleVersion, description, action, className }) {
  return (
    <div className={cn("border border-slate-300 bg-slate-50 rounded-sm p-3", className)}>
      <div className="flex items-center gap-2 mb-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-slate-700" strokeWidth={2.25} />
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">
          Deterministic decision · rules engine
        </span>
      </div>
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
        <span className="text-slate-500 uppercase tracking-widest text-[10px]">Rule</span>
        <span className="font-mono text-slate-900">{ruleId} <span className="text-slate-400">· {ruleVersion}</span></span>
        <span className="text-slate-500 uppercase tracking-widest text-[10px]">Action</span>
        <span className="font-mono text-slate-900">{action}</span>
        <span className="text-slate-500 uppercase tracking-widest text-[10px]">Logic</span>
        <span className="text-slate-700">{description}</span>
      </div>
    </div>
  );
}

/**
 * AI narrative placeholder — clearly labelled, never a decision.
 * Soft rounded edges, subtle teal border, explicit placeholder state.
 */
export function AiNarrativePlaceholder({ narrative, model, generatedAt, isPlaceholder = true, onGenerate, generating = false, className }) {
  return (
    <div className={cn(
      "border border-teal-200 bg-gradient-to-br from-white to-teal-50/40 rounded-md p-3",
      className
    )}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-altara-teal" strokeWidth={2.25} />
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-altara-teal">
            AI narrative · explains, does not decide
          </span>
        </div>
        {isPlaceholder ? (
          <span className="text-[10px] uppercase tracking-[0.12em] font-medium bg-teal-100/70 text-teal-800 px-2 py-0.5 rounded-sm border border-teal-200">
            PLACEHOLDER
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.12em] font-medium bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded-sm border border-emerald-200">
            AI · GENERATED
          </span>
        )}
      </div>
      {isPlaceholder ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-500 italic">
            AI narrative not yet generated for this recommendation. When generated, this area will show a plain-language
            explanation citing the supporting evidence — AI never overrides the deterministic rule outcome.
          </p>
          {onGenerate && (
            <button
              data-testid="ai-generate-narrative"
              onClick={onGenerate}
              disabled={generating}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1.5 border border-altara-teal text-altara-teal hover:bg-altara-teal hover:text-white rounded-sm transition-colors disabled:opacity-60 disabled:cursor-progress"
            >
              <Sparkles className="w-3 h-3" />
              {generating ? "Generating…" : "Explain with AI"}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          <p className="text-xs text-slate-700 leading-relaxed">{narrative}</p>
          <div className="text-[10px] font-mono text-slate-400 flex items-center gap-2">
            <span>model: {model}</span>
            <span>·</span>
            <span>generated: {generatedAt ? new Date(generatedAt).toLocaleString() : "—"}</span>
            {onGenerate && (
              <button
                data-testid="ai-regenerate-narrative"
                onClick={onGenerate}
                disabled={generating}
                className="ml-auto text-altara-teal hover:text-altara-deep underline decoration-dotted disabled:opacity-60"
              >
                {generating ? "regenerating…" : "regenerate"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
